import type { ReactNode } from "react";
import type { DataElScope } from "@/utils/dataEl";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import {
  IconArrowFork,
  IconFile,
  IconMessages,
  IconTextRecognition,
  IconUser,
} from "@tabler/icons-react";
import { Cicon } from "../Cicon";
import type { CmembericonProps } from "../Cmembericon";
import { Cprofileimage } from "../Cprofileimage";
import { ColorType } from "@/theme/colors";
import { CREFERENCE_ENTITY_TYPE, type CreferenceEntityType } from "./CreferenceEntity";
import { CreferenceMemberLeading } from "./CreferenceMemberLeading";

const ENTITY_FALLBACK_ICONS: Record<CreferenceEntityType, TablerIcon> = {
  [CREFERENCE_ENTITY_TYPE.PRODUCT]: IconFile,
  [CREFERENCE_ENTITY_TYPE.VERSION]: IconTextRecognition,
  [CREFERENCE_ENTITY_TYPE.FILE]: IconFile,
  [CREFERENCE_ENTITY_TYPE.MEMBER]: IconUser,
  [CREFERENCE_ENTITY_TYPE.COLLAB]: IconMessages,
  [CREFERENCE_ENTITY_TYPE.USER]: IconUser,
  [CREFERENCE_ENTITY_TYPE.UNKNOWN]: IconFile,
};

type CreferenceLeadingRenderer = (
  input: CreferenceLeadingProps,
) => ReactNode;

const LEADING_RENDERER_BY_TYPE: Record<
  CreferenceEntityType,
  CreferenceLeadingRenderer
> = {
  [CREFERENCE_ENTITY_TYPE.PRODUCT]: renderFallbackIcon,
  [CREFERENCE_ENTITY_TYPE.VERSION]: renderFallbackIcon,
  [CREFERENCE_ENTITY_TYPE.FILE]: renderFallbackIcon,
  [CREFERENCE_ENTITY_TYPE.MEMBER]: renderMemberLeading,
  [CREFERENCE_ENTITY_TYPE.COLLAB]: renderFallbackIcon,
  [CREFERENCE_ENTITY_TYPE.USER]: renderUserLeading,
  [CREFERENCE_ENTITY_TYPE.UNKNOWN]: renderFallbackIcon,
};

export interface CreferenceLeadingProps {
  dataElScope: DataElScope;
  currentUserImageSrc: string | null;
  entityType: CreferenceEntityType;
  hasMember: boolean;
  isCurrentUserReference: boolean;
  memberColor: string | null;
  memberProviderIconPath: string;
  memberProviderSlug: string;
  memberRole: CmembericonProps["role"] | null;
  productIcon: TablerIcon | null;
  isDecisionProduct: boolean;
}

export function CreferenceLeading({
  dataElScope,
  currentUserImageSrc,
  entityType,
  hasMember,
  isCurrentUserReference,
  memberColor,
  memberProviderIconPath,
  memberProviderSlug,
  memberRole,
  productIcon,
  isDecisionProduct,
}: CreferenceLeadingProps): ReactNode {
  return LEADING_RENDERER_BY_TYPE[entityType]({
    dataElScope,
    currentUserImageSrc,
    entityType,
    hasMember,
    isCurrentUserReference,
    memberColor,
    memberProviderIconPath,
    memberProviderSlug,
    memberRole,
    productIcon,
    isDecisionProduct,
  });
}

function renderMemberLeading(input: CreferenceLeadingProps): ReactNode {
  if (!input.hasMember) {
    return renderFallbackIcon(input);
  }

  return (
    <CreferenceMemberLeading
      dataElScope={input.dataElScope}
      memberColor={input.memberColor}
      memberProviderIconPath={input.memberProviderIconPath}
      memberProviderSlug={input.memberProviderSlug}
      memberRole={input.memberRole}
    />
  );
}

function renderUserLeading(input: CreferenceLeadingProps): ReactNode {
  if (!input.isCurrentUserReference || !input.currentUserImageSrc) {
    return renderFallbackIcon(input);
  }

  return (
    <Cprofileimage
      dataElScope={input.dataElScope}
      src={input.currentUserImageSrc}
      size={16}
      borderWidth={1}
      borderOpacity={0.3}
    />
  );
}

function renderFallbackIcon(input: CreferenceLeadingProps): ReactNode {
  // A decision node shows the fork ALONE (issue 3jhe6pk) — never appended
  // beside another type icon.
  if (input.isDecisionProduct) {
    return (
      <Cicon
        icon={IconArrowFork}
        size={15}
        color={ColorType.White}
        opacity={0.95}
      />
    );
  }

  return (
    <Cicon
      icon={input.productIcon ?? ENTITY_FALLBACK_ICONS[input.entityType]}
      size={15}
      color={ColorType.White}
      opacity={0.95}
    />
  );
}
